import { useTicketSubmissionMutation } from '@features/redux/apis/tickets';
import { useState } from 'react';
import { useQuery } from '@hooks/useQuery';
import { Box, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { Validate } from '@utils/validate';
import { TicketContainer } from '../TicketContainer';
import { TicketEffects } from './Effects';
import { Button } from '@components/design-system/components/Button/Button';
import { DraftTextField } from '@components/design-system/components/Form/DraftTextField';
import { DraftSelectField } from '@components/design-system/components/Form/DraftSelectField';
import { colors, typography, spacing } from '@components/design-system/tokens';

const issueTypes = [
  { label: 'General', value: 'standard' },
  { label: 'Network', value: 'network-lockout' },
];

export const Tickets = () => {
  const [submitTicket, mutationState] = useTicketSubmissionMutation({
    fixedCacheKey: 'SubmitTicketMutation',
  });

  const queryStrings = useQuery();

  // Track which fields were pre-populated via query params / localStorage
  const prefilled = {
    ticketType: Boolean(queryStrings.get('ticketType')),
    serialNum: Boolean(queryStrings.get('serialNum')),
    locationId: Boolean(queryStrings.get('locationId')),
  };

  const draft = useState({
    ticketType: queryStrings.get('ticketType') || '',
    serialNum: queryStrings.get('serialNum') || '',
    locationId: queryStrings.get('locationId') || '',
    locationName: '',
    customerEmail: '',
    customerName: '',
    customerPhone: '',
    description: '',
    withCustomer: false,
    ipAddress: '',
    networkStatus: '',
  } as Record<string, any>);

  const [draftState, setDraftState] = draft;

  return (
    <>
      <TicketEffects draft={draft} />

      <TicketContainer>
        <Box>
          <Typography sx={{
            fontFamily: typography.fontFamily.display,
            fontSize: { xs: typography.fontSize.xl, sm: typography.fontSize['2xl'] },
            fontWeight: typography.fontWeight.semibold,
            color: colors.neutral[900],
            textAlign: 'center',
          }}>
            Open a Support Ticket
          </Typography>
          <Typography sx={{
            fontSize: { xs: typography.fontSize.xs, sm: typography.fontSize.sm },
            color: colors.neutral[500],
            textAlign: 'center',
            mt: `${spacing.xs}px`,
          }}>
            Please be as specific and thorough as possible. A team member will contact you during business hours (Mon-Fri, 3:00 AM - 5:00 PM ET).
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${spacing.md}px` }}>
          {!prefilled.ticketType && (
            <DraftSelectField
              label="Issue Category"
              stateKey="ticketType"
              draft={draft}
              options={issueTypes}
              placeholder="Select a category"
              defaultFirst={false}
              required
            />
          )}

          <DraftTextField
            label="Your Name"
            stateKey="customerName"
            draft={draft}
            validateFn={Validate.name}
            helperText={{ valid: '', invalid: 'Name is required' }}
            required
          />

          <DraftTextField
            label="Email"
            stateKey="customerEmail"
            draft={draft}
            type="email"
            validateFn={Validate.Auth.email}
            helperText={{ valid: '', invalid: 'Please enter a valid email' }}
            required
          />

          <DraftTextField
            label="Phone (include country code)"
            stateKey="customerPhone"
            draft={draft}
            type="tel"
          />

          {!prefilled.serialNum && (
            <DraftTextField
              label="RapidCut Serial Number"
              stateKey="serialNum"
              draft={draft}
              validateFn={Validate.name}
              helperText={{ valid: '', invalid: 'Serial number is required' }}
              required
            />
          )}

          {!prefilled.locationId && (
            <>
              <DraftTextField
                label="Tablet ID"
                stateKey="locationId"
                draft={draft}
                validateFn={Validate.name}
                helperText={{ valid: '', invalid: 'Tablet ID is required' }}
                required
              />
              <DraftTextField
                label="Company Name"
                stateKey="companyName"
                draft={draft}
              />
              <DraftTextField
                label="Location Name (include mall or store name)"
                stateKey="locationName"
                draft={draft}
              />
            </>
          )}

          <DraftTextField
            label="Description"
            stateKey="description"
            draft={draft}
            multiline
            rows={4}
            placeholder="Describe the issue..."
            required
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(draftState.withCustomer)}
                onChange={(e) => setDraftState((prev) => ({ ...prev, withCustomer: e.target.checked }))}
                sx={{
                  color: colors.neutral[300],
                  '&.Mui-checked': { color: colors.primary[500] },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.neutral[700] }}>
                Are you with a customer right now?
              </Typography>
            }
          />

          <Button
            variant="primary"
            size="large"
            fullWidth
            loading={mutationState.isLoading}
            disabled={
              !Validate.Auth.email(draftState.customerEmail) ||
              !Validate.name(draftState.description)
            }
            onClick={() => submitTicket({ ...draftState })}
          >
            Submit Ticket
          </Button>
        </Box>
      </TicketContainer>
    </>
  );
};
